"use client";

import { isElementMarked } from "./2PGBN2Y4.js";
import { usePreviousMouseDownRef } from "./HLTQOHKZ.js";
import { isSafariFocusAncestor } from "./LVA2YJMS.js";
import { useStoreState } from "./YV4JVR4I.js";
import { useEvent, useSafeLayoutEffect } from "./ABQUS43J.js";
import { __spreadProps, __spreadValues } from "./3YLGPPWQ.js"; // src/dialog/utils/use-hide-on-interact-outside.ts

import { contains, getDocument, isVisible } from "@ariakit/core/utils/dom";
import { addGlobalEventListener } from "@ariakit/core/utils/events";
import { useEffect, useRef } from "react";

function isInDocument(target) {
  if (target.tagName === "HTML") return true;
  return contains(getDocument(target).body, target);
}

function isDisclosure(disclosure, target) {
  if (!disclosure) return false;
  if (contains(disclosure, target)) return true;
  var activeId = target.getAttribute("aria-activedescendant");

  if (activeId) {
    var activeElement = getDocument(disclosure).getElementById(activeId);

    if (activeElement) {
      return contains(disclosure, activeElement);
    }
  }

  return false;
}

function isMouseEventOnDialog(event, dialog) {
  if (!("clientY" in event)) return false;
  var rect = dialog.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;
  return rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;
}

function useEventOutside(_ref) {
  var store = _ref.store,
      type = _ref.type,
      listener = _ref.listener,
      capture = _ref.capture,
      domReady = _ref.domReady;
  var callListener = useEvent(listener);
  var open = useStoreState(store, "open");
  var focusedRef = useRef(false);
  useSafeLayoutEffect(function () {
    if (!open) return;
    if (!domReady) return;

    var _store$getState = store.getState(),
        contentElement = _store$getState.contentElement;

    if (!contentElement) return;

    var onFocus = function onFocus() {
      focusedRef.current = true;
    };

    contentElement.addEventListener("focusin", onFocus, true);
    return function () {
      return contentElement.removeEventListener("focusin", onFocus, true);
    };
  }, [store, open, domReady]);
  useEffect(function () {
    if (!open) return;

    var onEvent = function onEvent(event) {
      var _store$getState2 = store.getState(),
          contentElement = _store$getState2.contentElement,
          disclosureElement = _store$getState2.disclosureElement;

      var target = event.target;
      if (!contentElement) return;
      if (!target) return;
      if (!isInDocument(target)) return;
      if (contains(contentElement, target)) return;
      if (isDisclosure(disclosureElement, target)) return;
      if (target.hasAttribute("data-focus-trap")) return;
      if (isMouseEventOnDialog(event, contentElement)) return;
      var focused = focusedRef.current;
      if (focused && !isElementMarked(target, contentElement.id)) return;
      if (isSafariFocusAncestor(target)) return;
      callListener(event);
    };

    return addGlobalEventListener(type, onEvent, capture);
  }, [open, capture]);
}

function shouldHideOnInteractOutside(hideOnInteractOutside, event) {
  if (typeof hideOnInteractOutside === "function") {
    return hideOnInteractOutside(event);
  }

  return !!hideOnInteractOutside;
}

function useHideOnInteractOutside(store, hideOnInteractOutside, domReady) {
  var open = useStoreState(store, "open");
  var previousMouseDownRef = usePreviousMouseDownRef(open);
  var props = {
    store: store,
    domReady: domReady,
    capture: true
  };
  useEventOutside(__spreadProps(__spreadValues({}, props), {
    type: "click",
    listener: function listener(event) {
      var _store$getState3 = store.getState(),
          contentElement = _store$getState3.contentElement;

      var previousMouseDown = previousMouseDownRef.current;
      if (!previousMouseDown) return;
      if (!isVisible(previousMouseDown)) return;
      if (!isElementMarked(previousMouseDown, contentElement == null ? void 0 : contentElement.id)) return;
      if (!shouldHideOnInteractOutside(hideOnInteractOutside, event)) return;
      store.hide();
    }
  }));
  useEventOutside(__spreadProps(__spreadValues({}, props), {
    type: "focusin",
    listener: function listener(event) {
      var _store$getState4 = store.getState(),
          contentElement = _store$getState4.contentElement;

      if (!contentElement) return;
      if (event.target === getDocument(contentElement)) return;
      if (!shouldHideOnInteractOutside(hideOnInteractOutside, event)) return;
      store.hide();
    }
  }));
  useEventOutside(__spreadProps(__spreadValues({}, props), {
    type: "contextmenu",
    listener: function listener(event) {
      if (!shouldHideOnInteractOutside(hideOnInteractOutside, event)) return;
      store.hide();
    }
  }));
}

export { useHideOnInteractOutside };